// FeedComponent.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import UniversalComponent from '../component/UniversalComponent.jsx';
import WithIntersectionObserver from '../component/WithIntersectionObserver.jsx';

import {
  HomeCategoryUi,
  CategoryLayout,
  CategoryComponentUi,
  PSEUDO_TRENDING_HASHTAG,
  PSEUDO_TRENDING_CATEGORY,
  PSEUDO_LEADERBOARD,
  PSEUDO_SHOWCASE,
  FeedCardSize,
} from '../resource/feedConstants.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { breakpoint } from '../style/variables.js';

// Constants for placeholder heights
// Header: Title min-height(30px, mobile 27px) + margin-bottom(4px)
const HEADER_HEIGHT = 34;
const HEADER_HEIGHT_MOBILE = 31;
const {
  MESSAGE_CARD_HEIGHT,
  MESSAGE_CARD_HEIGHT_DESKTOP,
  SHORTS_CARD_HEIGHT,
  SHORTS_CARD_HEIGHT_DESKTOP,
  PROFILE_CARD_HEIGHT,
  PROFILE_CARD_HEIGHT_DESKTOP,
} = FeedCardSize;
const BANNER_HEIGHT = 136; // HomeBanner.jsx min-height: 136px
const BANNER_WITH_GAP_HEIGHT = BANNER_HEIGHT + 16;
const LIVESTREAM_ROW_HEIGHT = 262; // HomeLiveStreamCategory.jsx: calc(302px - SCROLL_BAR_HEIGHT)
const USER_ROW_HEIGHT = 180; // UserCategory.jsx StyledUserCategory CSS height: 180px (with header)
const HASHTAG_CATEGORY_HEIGHT = 60; // HashtagCategoryItem: 36px(desktop)/28px(mobile) + padding
const SHOWCASE_HEIGHT = 260; // variables.js: mobile 240px, tablet 300px, desktop 522px
const LEADERBOARD_HEIGHT = 350;

// Pseudo category height mapping
const PSEUDO_HEIGHT_PATTERNS = [
  { pattern: /^pseudo_(video_)?banner$/, height: BANNER_WITH_GAP_HEIGHT },
  // Widesize banner has aspect-ratio responsive height. Use 0 so the wrapper
  // tracks the article's natural size instead of locking in a desktop-too-tall
  // placeholder. The article itself reserves space via aspect-ratio.
  { pattern: /^pseudo_wide_banner$/, height: 0 },
  {
    pattern: new RegExp(
      `^(${PSEUDO_TRENDING_CATEGORY}|${PSEUDO_TRENDING_HASHTAG})$`
    ),
    height: HASHTAG_CATEGORY_HEIGHT,
  },
  {
    pattern: new RegExp(`^${PSEUDO_LEADERBOARD}$`),
    height: LEADERBOARD_HEIGHT,
  },
  { pattern: new RegExp(`^${PSEUDO_SHOWCASE}$`), height: SHOWCASE_HEIGHT },
  {
    pattern: /^pseudo_following_stories/i,
    height: HEADER_HEIGHT + MESSAGE_CARD_HEIGHT,
  },
  {
    pattern: /^pseudo_following/i,
    height: USER_ROW_HEIGHT,
  },
];

const responsiveMinHeight = height => {
  if (typeof height === 'number') return `min-height: ${height}px;`;
  const { desktop, laptop, tablet, mobile } = height;
  return `
    min-height: ${mobile}px;
    @media (min-width: ${breakpoint.mobile + 1}px) {
      min-height: ${tablet}px;
    }
    @media (min-width: ${breakpoint.tablet + 1}px) {
      min-height: ${laptop}px;
    }
    @media (min-width: ${breakpoint.laptop + 1}px) {
      min-height: ${desktop}px;
    }
  `;
};

// Square card row heights (responsive)
// Height = card_width(1:1 aspect) + metadata_min_height + header_height(isSquare)
// Desktop(>1440): 340 + 56 + 34 = 430
// Laptop(1024~1440): 280 + 56 + 34 = 370
// Tablet(768~1024): 220 + 44 + 34 = 298
// Mobile(<=768): 180 + 44 + 31 = 255
const USER_SQUARE_ROW_HEIGHTS = {
  desktop: 430,
  laptop: 370,
  tablet: 298,
  mobile: 255,
};

// Double row grid heights = GridListWrapper max-height + header
// On <=1024 falls back to row layout (same as USER_SQUARE_ROW_HEIGHTS)
const USER_SQUARE_DOUBLE_ROW_HEIGHTS = {
  desktop: 874, // (340+4+24+12+28+8)*2+8 + 34
  laptop: 754, // (280+4+24+12+28+8)*2+8 + 34
  tablet: 298, // row layout fallback
  mobile: 255, // row layout fallback
};
const LEADERBOARD_SQUARE_DOUBLE_ROW_HEIGHTS = {
  desktop: 892, // (340+4+33+12+28+8)*2+8 + 34
  laptop: 772, // (280+4+33+12+28+8)*2+8 + 34
  tablet: 298, // row layout fallback
  mobile: 255, // row layout fallback
};

// DiscoverFlixRow.jsx responsive min-heights
const FLIX_ROW_HEIGHTS = {
  desktop: 372,
  laptop: 372,
  tablet: 274,
  mobile: 360,
};

// ROW + MESSAGE_CARD + header
// Card sizes switch at media.tablet(<=1024), header at media.mobile(<=768)
const STORY_ROW_HEIGHTS = {
  desktop: HEADER_HEIGHT + MESSAGE_CARD_HEIGHT_DESKTOP, // 34 + 198 = 232
  laptop: HEADER_HEIGHT + MESSAGE_CARD_HEIGHT_DESKTOP, // 34 + 198 = 232
  tablet: HEADER_HEIGHT + MESSAGE_CARD_HEIGHT, // 34 + 172 = 206
  mobile: HEADER_HEIGHT_MOBILE + MESSAGE_CARD_HEIGHT, // 31 + 172 = 203
};

// ROW + SHORTS_CARD + header
const SHORTS_ROW_HEIGHTS = {
  desktop: HEADER_HEIGHT + SHORTS_CARD_HEIGHT_DESKTOP, // 34 + 320 = 354
  laptop: HEADER_HEIGHT + SHORTS_CARD_HEIGHT_DESKTOP, // 34 + 320 = 354
  tablet: HEADER_HEIGHT + SHORTS_CARD_HEIGHT, // 34 + 240 = 274
  mobile: HEADER_HEIGHT_MOBILE + SHORTS_CARD_HEIGHT, // 31 + 240 = 271
};

// ROW + PROFILE_CARD_ITEM + header (STORY_METADATA_CARD, SHORTS_METADATA_CARD)
const PROFILE_CARD_ROW_HEIGHTS = {
  desktop: HEADER_HEIGHT + PROFILE_CARD_HEIGHT_DESKTOP, // 34 + 398.91 = 432.91
  laptop: HEADER_HEIGHT + PROFILE_CARD_HEIGHT_DESKTOP, // 34 + 398.91 = 432.91
  tablet: HEADER_HEIGHT + PROFILE_CARD_HEIGHT, // 34 + 296.42 = 330.42
  mobile: HEADER_HEIGHT_MOBILE + PROFILE_CARD_HEIGHT, // 31 + 296.42 = 327.42
};

// UI type to height mapping (only predictable ROW layouts)
// GRID/LIST types are excluded — they use hasUnpredictableHeight
const UI_HEIGHT_MAP = {
  [HomeCategoryUi.FLIX]: FLIX_ROW_HEIGHTS,
  [HomeCategoryUi.LIVESTREAM]: LIVESTREAM_ROW_HEIGHT,
  [HomeCategoryUi.USER]: USER_ROW_HEIGHT,
  [HomeCategoryUi.USER_SQUARE]: USER_SQUARE_ROW_HEIGHTS,
  [HomeCategoryUi.USER_SQUARE_DOUBLE_ROW]: USER_SQUARE_DOUBLE_ROW_HEIGHTS,
  [HomeCategoryUi.LEADERBOARD_USER_SQUARE]: USER_SQUARE_ROW_HEIGHTS,
  [HomeCategoryUi.LEADERBOARD_USER_SQUARE_DOUBLE_ROW]:
    LEADERBOARD_SQUARE_DOUBLE_ROW_HEIGHTS,
  [HomeCategoryUi.STORY]: STORY_ROW_HEIGHTS,
  [HomeCategoryUi.SHORTS]: SHORTS_ROW_HEIGHTS,
  [HomeCategoryUi.STORY_METADATA_CARD]: PROFILE_CARD_ROW_HEIGHTS,
  [HomeCategoryUi.SHORTS_METADATA_CARD]: PROFILE_CARD_ROW_HEIGHTS,
};

const DEFAULT_HEIGHT = STORY_ROW_HEIGHTS;

/**
 * Get placeholder height based on UI type.
 * @param {string} ui - HomeCategoryUi type
 * @param {string} category - category string
 * @returns {number} placeholder height (px)
 */
export const getPlaceholderHeight = (ui, category) => {
  // Handle pseudo categories
  if (/^pseudo_/i.test(category)) {
    const matched = PSEUDO_HEIGHT_PATTERNS.find(({ pattern }) =>
      pattern.test(category)
    );
    return matched?.height ?? 200;
  }

  // Handle normal feed types — return raw height config (number or object)
  return UI_HEIGHT_MAP[ui] ?? DEFAULT_HEIGHT;
};

export const FeedPlaceholder = styled.div`
  ${({ height }) =>
    typeof height === 'number'
      ? `height: ${height}px;`
      : `
    height: ${height.mobile}px;
    @media (min-width: ${breakpoint.mobile + 1}px) {
      height: ${height.tablet}px;
    }
    @media (min-width: ${breakpoint.tablet + 1}px) {
      height: ${height.laptop}px;
    }
    @media (min-width: ${breakpoint.laptop + 1}px) {
      height: ${height.desktop}px;
    }
  `}
  background: transparent;
`;

/**
 * @typedef ParsedCategory
 * @property {string} ui - indicate which ui component should use.
 * @property {string} category - category string.
 */

/**
 * Parse category string.
 * @param {string} {category} - category string.
 * @param {boolean} {isLivestreamFeed} - indicate category is livestream feed or not.
 * @param {boolean} {isLastCategory} - indicate category is last item in categories.
 * @return {ParsedCategory}
 */
export const parseCategoryString = ({
  category,
  isLivestreamFeed,
  isLastCategory,
} = {}) => {
  const url = new URL(category, 'https://swag.live');
  let ui = url.searchParams.get('ui') || url.searchParams.get('_ui');
  if (!ui) {
    const isPseudo = /^pseudo_/i.test(category);
    if (isPseudo) {
      ui = undefined;
    } else if (isLastCategory) {
      if (/^(flix|posts)_/.test(category)) {
        ui = HomeCategoryUi.FLIX_GRID;
      } else if (/^livestream_|^user_/.test(category)) {
        ui = HomeCategoryUi.LIVESTREAM_GRID;
      } else {
        ui = HomeCategoryUi.STORY_GRID;
      }
    } else if (/^livestream_/.test(category) || isLivestreamFeed) {
      ui = HomeCategoryUi.LIVESTREAM;
    } else if (/^post_image_/.test(category)) {
      ui = HomeCategoryUi.POST_CARD_GRID;
    } else if (/^(flix|posts|post_video)_/.test(category)) {
      ui = HomeCategoryUi.FLIX;
    } else if (/^story_|stories_|^home$/.test(category)) {
      ui = HomeCategoryUi.STORY;
    } else if (/^short_by_category_/.test(category)) {
      ui = HomeCategoryUi.SHORTS_METADATA_CARD;
    } else if (/^shorts_/.test(category)) {
      ui = HomeCategoryUi.SHORTS;
    } else if (/^user/.test(category)) {
      ui = HomeCategoryUi.USER;
    } else {
      ui = HomeCategoryUi.STORY;
    }
  }
  return {
    category: decodeURIComponent(
      `${url.pathname.replace(/^\//, '')}${url.search}`
    ),
    ui,
  };
};

/**
 * Get user category component's props.
 * @param {HomeCategoryUi} {ui} - category ui.
 * @return {object} Return component props.
 */
const getUserCategoryProps = ({ ui } = {}) => {
  const props = {};
  switch (ui) {
    case HomeCategoryUi.USER_GRID: {
      props.withHalo = true;
      props.isGrid = true;
      break;
    }
    case HomeCategoryUi.USER_SQUARE: {
      props.isSquare = true;
      break;
    }
    case HomeCategoryUi.USER_SQUARE_DOUBLE_ROW: {
      props.isSquare = true;
      props.isDoubleRow = true;
      break;
    }
    case HomeCategoryUi.USER_SQUARE_GRID: {
      props.isSquare = true;
      props.isGrid = true;
      break;
    }
    case HomeCategoryUi.LEADERBOARD_USER_SQUARE: {
      props.isSquare = true;
      props.isLeaderboard = true;
      break;
    }
    case HomeCategoryUi.LEADERBOARD_USER_SQUARE_DOUBLE_ROW: {
      props.isSquare = true;
      props.isLeaderboard = true;
      props.isDoubleRow = true;
      break;
    }
    case HomeCategoryUi.LEADERBOARD_USER_SQUARE_GRID: {
      props.isSquare = true;
      props.isLeaderboard = true;
      props.isGrid = true;
      break;
    }
    case HomeCategoryUi.USER:
    default: {
      props.withHalo = true;
      break;
    }
  }
  return props;
};

/**
 * get feed component props (not include lazy load logic)
 * @param {object} options
 * @returns {object|null} componentProps or null if invalid
 */
const getFeedComponentProps = ({
  category,
  livestreamFeed,
  titleI18nId,
  isLastCategory,
}) => {
  const componentProps = {};
  const parsed = parseCategoryString({
    category,
    isLivestreamFeed: category === livestreamFeed,
    isLastCategory,
  });
  componentProps.category = parsed.category;
  componentProps.titleI18nId =
    titleI18nId || getTranslationKey({ key: category });
  componentProps.ui = parsed.ui;

  const isPseudo = /^pseudo_/i.test(category);
  if (isPseudo) {
    switch (true) {
      case 'pseudo_banner' === category:
      case 'pseudo_video_banner' === category: {
        componentProps.filename = 'Container_HomeBanner';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/HomeBanner.js');
        break;
      }
      case 'pseudo_wide_banner' === category: {
        componentProps.filename = 'Container_WidesizeBanner';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/WidesizeBanner.js');
        break;
      }
      case [PSEUDO_TRENDING_CATEGORY, PSEUDO_TRENDING_HASHTAG].includes(
        category
      ): {
        componentProps.filename = 'Container_HashtagCategoryList';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/HashtagCategoryList.js');
        break;
      }
      case PSEUDO_LEADERBOARD === category: {
        componentProps.filename = 'Container_HomeLeaderboard';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/HomeLeaderboard.js');
        break;
      }
      case PSEUDO_SHOWCASE === category: {
        componentProps.filename = 'Container_HomeShowcaseList';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/HomeShowcaseList.js');
        componentProps.shouldUseLazyImage = false;
        break;
      }
      case 'pseudo_livestream_showcase' === category: {
        componentProps.filename = 'Container_LivestreamShowcaseList';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/LivestreamShowcaseList.js');
        break;
      }
      case 'pseudo_freezone_button' === category: {
        componentProps.filename = 'Component_FreezoneLink';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../component/FreezoneLink.jsx');
        break;
      }
      case 'pseudo_livestream_button' === category: {
        componentProps.filename = 'Component_LivestreamLink';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../component/LivestreamLink.jsx');
        break;
      }
      case /^pseudo_following_stories/i.test(category): {
        componentProps.filename = 'Container_FollowingStoryRow';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FollowingStoryRow.js');
        componentProps.shouldShowTitle = true;
        componentProps.shouldShowMoreLink = true;
        componentProps.layout = CategoryLayout.ROW;
        componentProps.componentUi = CategoryComponentUi.MESSAGE_CARD;
        break;
      }
      case /^pseudo_following/i.test(category): {
        componentProps.filename = 'Container_UserCategory';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/UserCategory.js');
        componentProps.isFollowing = true;
        const categoryProps = getUserCategoryProps({ ui: parsed.ui });
        Object.keys(categoryProps).forEach(
          key => (componentProps[key] = categoryProps[key])
        );
        break;
      }
      default: {
        return null;
      }
    }
  } else {
    switch (parsed.ui) {
      case HomeCategoryUi.FLIX_GRID: {
        componentProps.filename = 'Container_DiscoverFlixMoreRow';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/DiscoverFlixMoreRow.js');
        break;
      }
      case HomeCategoryUi.FLIX_GRID_MOBILE_FLIX_LIST: {
        componentProps.filename = 'Container_DiscoverFlixMoreRow';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/DiscoverFlixMoreRow.js');
        componentProps.shouldShowListLayoutOnMobile = true;
        break;
      }
      case HomeCategoryUi.FLIX: {
        componentProps.filename = 'Container_DiscoverFlixRow';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/DiscoverFlixRow.js');
        break;
      }
      case HomeCategoryUi.FLIX_LIST: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.layout = CategoryLayout.LIST;
        componentProps.componentUi = CategoryComponentUi.FLIX_LIST_ITEM;
        break;
      }
      case HomeCategoryUi.LIVESTREAM_GRID:
      case HomeCategoryUi.LIVESTREAM_SQUARE: {
        componentProps.filename = 'Container_HomeLiveStreamFeed';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/HomeLiveStreamFeed.js');
        componentProps.isSquare =
          HomeCategoryUi.LIVESTREAM_SQUARE === parsed.ui;
        break;
      }
      case HomeCategoryUi.LIVESTREAM_TOP: {
        componentProps.filename = 'Container_TopHomeLiveStreamFeed';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/TopHomeLiveStreamFeed.js');
        break;
      }
      case HomeCategoryUi.LIVESTREAM: {
        componentProps.filename = 'Container_HomeLiveStreamCategory';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/HomeLiveStreamCategory.js');
        break;
      }
      case HomeCategoryUi.USER:
      case HomeCategoryUi.USER_GRID:
      case HomeCategoryUi.USER_SQUARE:
      case HomeCategoryUi.USER_SQUARE_DOUBLE_ROW:
      case HomeCategoryUi.USER_SQUARE_GRID:
      case HomeCategoryUi.LEADERBOARD_USER_SQUARE:
      case HomeCategoryUi.LEADERBOARD_USER_SQUARE_DOUBLE_ROW:
      case HomeCategoryUi.LEADERBOARD_USER_SQUARE_GRID: {
        componentProps.filename = 'Container_UserCategory';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/UserCategory.js');
        const categoryProps = getUserCategoryProps({ ui: parsed.ui });
        Object.keys(categoryProps).forEach(
          key => (componentProps[key] = categoryProps[key])
        );
        break;
      }
      case HomeCategoryUi.SHORTS: {
        componentProps.filename = 'Container_ShortsRow';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/ShortsRow.js');
        componentProps.shouldShowTitle = true;
        componentProps.shouldShowMoreLink = true;
        componentProps.layout = CategoryLayout.ROW;
        componentProps.componentUi = CategoryComponentUi.MESSAGE_CARD;
        break;
      }
      case HomeCategoryUi.SHORTS_METADATA_CARD: {
        componentProps.filename = 'Container_ShortsRow';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/ShortsRow.js');
        componentProps.shouldShowTitle = true;
        componentProps.shouldShowMoreLink = true;
        componentProps.layout = CategoryLayout.ROW;
        componentProps.componentUi = CategoryComponentUi.PROFILE_CARD_ITEM;
        break;
      }
      case HomeCategoryUi.SHORTS_METADATA_CARD_GRID: {
        componentProps.filename = 'Container_ShortsGrid';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/ShortsGrid.js');
        componentProps.layout = CategoryLayout.GRID;
        componentProps.componentUi = CategoryComponentUi.PROFILE_CARD_ITEM;
        break;
      }
      case HomeCategoryUi.POST_FEED: {
        componentProps.filename = 'Container_PostFeed';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/PostFeed.js');
        break;
      }
      case HomeCategoryUi.POST_CARD_GRID: {
        componentProps.filename = 'Container_PostCardCategory';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/PostCardCategory.js');
        break;
      }
      case HomeCategoryUi.STORY_GRID: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.layout = CategoryLayout.GRID;
        componentProps.componentUi = CategoryComponentUi.MESSAGE_CARD;
        break;
      }
      case HomeCategoryUi.STORY_METADATA_CARD: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.shouldShowTitle = true;
        componentProps.shouldShowMoreLink = true;
        componentProps.layout = CategoryLayout.ROW;
        componentProps.componentUi = CategoryComponentUi.PROFILE_CARD_ITEM;
        break;
      }
      case HomeCategoryUi.STORY_METADATA_CARD_GRID: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.layout = CategoryLayout.GRID;
        componentProps.componentUi = CategoryComponentUi.PROFILE_CARD_ITEM;
        break;
      }
      case HomeCategoryUi.MESSAGE_LIST: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.layout = CategoryLayout.LIST;
        componentProps.componentUi = CategoryComponentUi.OUTBOX_MESSAGE;
        componentProps.isImage = true;
        break;
      }
      case HomeCategoryUi.STORY_LIST: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.layout = CategoryLayout.LIST;
        componentProps.componentUi = CategoryComponentUi.OUTBOX_MESSAGE;
        break;
      }
      case HomeCategoryUi.STORY:
      default: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.shouldShowTitle = true;
        componentProps.shouldShowMoreLink = true;
        componentProps.layout = CategoryLayout.ROW;
        componentProps.componentUi = CategoryComponentUi.MESSAGE_CARD;
        break;
      }
    }
  }

  return componentProps;
};

export const FeedComponent = props => {
  const {
    title,
    livestreamFeed = null,
    category = null,
    titleI18nId = null,
    isLastCategory = false,
    needIntersectionObserver = true,
    intersectionObserverThreshold = 0.1,
    ...rest
  } = props;
  const { t } = useTranslation();

  const componentProps = getFeedComponentProps({
    category,
    livestreamFeed,
    titleI18nId,
    isLastCategory,
  });

  if (!componentProps) {
    return null;
  }

  const { ui } = componentProps;
  const minHeight = getPlaceholderHeight(ui, category);
  const hasUnpredictableHeight = [
    HomeCategoryUi.FLIX_GRID,
    HomeCategoryUi.FLIX_GRID_MOBILE_FLIX_LIST,
    HomeCategoryUi.FLIX_LIST,
    HomeCategoryUi.LIVESTREAM_GRID,
    HomeCategoryUi.LIVESTREAM_SQUARE,
    HomeCategoryUi.LIVESTREAM_TOP,
    HomeCategoryUi.USER_GRID,
    HomeCategoryUi.USER_SQUARE_GRID,
    HomeCategoryUi.LEADERBOARD_USER_SQUARE_GRID,
    HomeCategoryUi.STORY_GRID,
    HomeCategoryUi.STORY_METADATA_CARD_GRID,
    HomeCategoryUi.STORY_METADATA_CARD,
    HomeCategoryUi.SHORTS_METADATA_CARD,
    HomeCategoryUi.STORY_LIST,
    HomeCategoryUi.SHORTS_METADATA_CARD_GRID,
    HomeCategoryUi.POST_FEED,
    HomeCategoryUi.POST_CARD_GRID,
    HomeCategoryUi.MESSAGE_LIST,
  ].includes(ui);
  const shouldLazyLoad = needIntersectionObserver && !hasUnpredictableHeight;
  const composedTitle =
    title ||
    (componentProps.titleI18nId
      ? t(componentProps.titleI18nId, { ns: TranslationNamespace.FEED })
      : '');

  // Without IntersectionObserver, render directly
  if (!shouldLazyLoad) {
    return (
      <FeedWrapper minHeight={minHeight}>
        <UniversalComponent
          {...{ ...componentProps, title: composedTitle, ...rest }}
        />
      </FeedWrapper>
    );
  }

  // With IntersectionObserver for lazy load
  return (
    <WithIntersectionObserver threshold={intersectionObserverThreshold}>
      {({ isIntersected }) => (
        <FeedWrapper minHeight={minHeight}>
          {isIntersected ? (
            <UniversalComponent
              {...{ ...componentProps, title: composedTitle, ...rest }}
            />
          ) : (
            <FeedPlaceholder height={minHeight} />
          )}
        </FeedWrapper>
      )}
    </WithIntersectionObserver>
  );
};

const FeedWrapper = styled.div`
  ${({ minHeight }) => responsiveMinHeight(minHeight)}

  // if empty feed component
  /* TODO(safari-15-compat): :has() is Safari 15.4+; this min-height override is ignored on Safari 15.0–15.3 (the wrapper keeps the non-zero min-height even when empty). Either accept the graceful degradation or rewrite using a prop / class. */
  &:has(> :empty) {
    min-height: 0;
  }
`;

FeedComponent.propTypes = {
  title: PropTypes.string,
  category: PropTypes.string,
  titleI18nId: PropTypes.string,
  livestreamFeed: PropTypes.string,
  isLastCategory: PropTypes.bool,
  needIntersectionObserver: PropTypes.bool,
  intersectionObserverThreshold: PropTypes.number,
};

export default FeedComponent;
